import React from 'react';
import {
  Box,
  BoxProps,
  DialogActions as MUIDialogActions,
  DialogContent as MUIDialogContent,
  DialogContentProps,
  DialogTitle as MUIDialogTitle,
  styled,
  Typography,
  DialogTitleProps,
  useMediaQuery,
  Skeleton,
  Stack,
} from '@mui/material';

export const DialogTitle = ({ children, sx, ...props }: DialogTitleProps) => (
  <MUIDialogTitle sx={[{ px: 5, pt: 5, pb: 3 }, ...(Array.isArray(sx) ? sx : [sx])]} textAlign="center" {...props}>
    {children}
  </MUIDialogTitle>
);

export const DialogActions = styled(MUIDialogActions)({
  paddingTop: '24px',
  paddingLeft: '40px',
  paddingRight: '40px',
  paddingBottom: '40px',
});

const StyledContainer = styled(Box)({
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
});

/** Shared dialog body: MUI `DialogContent` plus optional inner scroll box overrides. */
export type SharedDialogContentProps = DialogContentProps & {
  slotProps?: {
    box?: BoxProps;
  };
};

export function DialogContent({ children, sx, slotProps, dividers = true, ...props }: SharedDialogContentProps) {
  return (
    <MUIDialogContent
      dividers={dividers}
      sx={[{ px: 5, py: 0, display: 'flex', overflow: 'hidden' }, ...(Array.isArray(sx) ? sx : [sx])]}
      {...props}
      data-cy="dialog-content"
    >
      <Box height="auto" overflow="auto" flex="1 1 auto" pr={3} mr={-3} py={2} {...slotProps?.box}>
        <Typography variant="body1" component="div">
          {children}
        </Typography>
      </Box>
    </MUIDialogContent>
  );
}

export const DialogContainer = ({
  children,
  size = 'M',
  slotProps,
}: {
  children: React.ReactNode;
  size?: 'S' | 'M' | 'L' | 'XL';
  slotProps?: BoxProps;
}) => {
  const pixelSize = { S: 444, M: 600, L: 900, XL: 1200 }[size];
  const isResponsive = useMediaQuery(`(max-width:${pixelSize + 64}px)`);
  return (
    <StyledContainer
      maxWidth={isResponsive ? '100%' : pixelSize}
      width={'100vw'}
      {...slotProps}
      data-cy="dialog-container"
    >
      {children}
    </StyledContainer>
  );
};

export const DialogSkeleton = ({ size = 'M' }: { size?: 'S' | 'M' | 'L' | 'XL' }) => (
  <DialogContainer size={size}>
    <DialogTitle>
      <Skeleton variant="text" width="100%" />
    </DialogTitle>
    <DialogContent>
      <Skeleton variant="text" width="100%" />
      <Skeleton variant="text" width="100%" />
      <Skeleton variant="text" width="30%" />
    </DialogContent>
    <DialogActions>
      <Stack direction="row" gap={2} width="100%">
        <Skeleton width="100%" height="32px" variant="rectangular" />
        <Skeleton width="100%" height="32px" variant="rectangular" />
      </Stack>
    </DialogActions>
  </DialogContainer>
);
